/**
 * hpq-silent-auth-init.js — CAA session bootstrap on page load.
 *
 * Two responsibilities:
 *
 *   1. **Login-popup relay**: when this script loads on the login popup's
 *      callback page (opened by startPkcePopupFlow), postMessage the OAuth
 *      code/state back to the opener — which owns the single token exchange —
 *      and close the popup.
 *
 *   2. **Silent token refresh**: on ordinary page loads, if the stored access/
 *      id token has expired but a refresh_token is present, silently refresh via
 *      the OAuth refresh_token grant (a plain token-endpoint POST — no iframe,
 *      no navigation) so the customer stays signed in. On success, fire
 *      HPQ_RECONCILE_AUTH_STATE() so the drawer flips Account/Logout.
 *
 * NOTE: the former hidden-iframe prompt=none silent-auth was removed 2026-08-18.
 * It could never work here — Shopify's authorize endpoint AND our own storefront
 * both send X-Frame-Options: DENY + CSP frame-ancestors 'none', so the callback
 * can never render inside an iframe to relay its code (verified empirically).
 * refresh_token is the supported, CSP-proof replacement.
 */
(function () {
  'use strict';

  var POPUP_CALLBACK_PARAM = 'fortune_oauth_callback';
  var POPUP_ACTIVE_KEY = 'hpq_caa_popup_active';

  // ── 1. Login-popup callback relay ────────────────────────────────────────────
  // The popup (opened by startPkcePopupFlow) lands here after Shopify redirects
  // back to our origin. Identify it by the shared-localStorage marker the opener
  // set (survives the cross-origin round-trip; Shopify can't touch it) plus a
  // live window.opener — so an ordinary tab that merely has an opener is never
  // mistaken for the popup. The OPENER owns the single token exchange, so we only
  // relay the code back and close. We also set an in-memory flag so the
  // fortune-footer callback handler (a separate, later-running script)
  // deterministically skips its own exchange.
  function maybeRelayPopupResult() {
    if (window.parent !== window) return false;                 // never inside an iframe
    if (!(window.opener && window.opener !== window)) return false;
    if (!popupMarkerPresent()) return false;
    var params = new URLSearchParams(window.location.search);
    if (params.get(POPUP_CALLBACK_PARAM) !== '1') return false;

    window.HPQ_POPUP_AUTH_RELAYED = true;
    try {
      window.opener.postMessage({
        type: 'hpq_popup_auth_result',
        code: params.get('code'),
        state: params.get('state'),
        error: params.get('error')
      }, window.location.origin);
    } catch (e) {}
    if (document.documentElement) document.documentElement.style.display = 'none';
    // Delay the close briefly so the postMessage is flushed before teardown.
    setTimeout(function () { try { window.close(); } catch (e) {} }, 100);
    return true;
  }

  function popupMarkerPresent() {
    try { return window.localStorage.getItem(POPUP_ACTIVE_KEY) === '1'; }
    catch (e) { return false; }
  }

  if (maybeRelayPopupResult()) return;

  // ── 2. Silent token refresh on page load ─────────────────────────────────────
  function getShopDomain() {
    return window.HPQ_CAA_SHOP_DOMAIN || window.location.hostname;
  }

  function reconcile() {
    if (typeof window.HPQ_RECONCILE_AUTH_STATE === 'function') {
      window.HPQ_RECONCILE_AUTH_STATE();
    }
  }

  function bootstrapSession() {
    var caa = window.HPQCustomerAccountAPI;
    if (!caa || !caa.getCachedTokens) return;

    // Skip on the OAuth callback page — token handling there is owned by the
    // fortune-footer callback handler (redirect) or the popup opener.
    var params = new URLSearchParams(window.location.search);
    if (params.get(POPUP_CALLBACK_PARAM) === '1') return;

    // Already have a valid (unexpired) session — just reconcile the drawer UI.
    if (caa.getCachedTokens()) { reconcile(); return; }

    // Expired/missing access token but a refresh_token on hand → silently refresh.
    // No refresh_token means this browser never completed a login here, so we
    // stay anonymous (the shopper can sign in via the popup).
    var clientId = window.HPQ_CAA_CLIENT_ID;
    if (!clientId || !caa.refreshAccessToken || !caa.getStoredRefreshToken) return;
    if (!caa.getStoredRefreshToken()) return;

    caa.refreshAccessToken({ shopDomain: getShopDomain(), clientId: clientId })
      .then(function (result) {
        if (result && result.ok) reconcile();
      });
  }

  function waitForCaa(retries) {
    if (window.HPQCustomerAccountAPI && window.HPQCustomerAccountAPI.getCachedTokens) {
      bootstrapSession();
      return;
    }
    if (retries <= 0) return;
    setTimeout(function () { waitForCaa(retries - 1); }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { waitForCaa(30); });
  } else {
    waitForCaa(30);
  }
})();
